import type { ProductOutput } from '@st/contract';
import { usePublicProducts } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatMoney } from '../../lib/format';
import { trialBadgeKey } from '../../lib/trial';
import { Badge, Button, Card, ProgressBar, SkeletonList } from '../../ui';

export interface ProgramStepProps {
  screenIndex: number;
  totalScreens: number;
  onChoose: (slug: string) => void;
  onBack: () => void;
  pendingSlug: string | null;
  error?: unknown;
}

/**
 * Treći korak: odabir programa. Kartice nose oznaku „prva N treninga besplatno”
 * jer je to jedini razlog zašto se program bira PRIJE plaćanja.
 */
export function ProgramStep({
  screenIndex,
  totalScreens,
  onChoose,
  onBack,
  pendingSlug,
  error,
}: ProgramStepProps) {
  const { t, locale } = useTranslation();
  const products = usePublicProducts(locale);

  return (
    <section className="rounded-2xl border border-line-subtle bg-surface p-7 shadow-panel">
      <p className="eyebrow mb-2">{t('wizard.program')}</p>
      <ProgressBar
        className="mb-6"
        value={screenIndex + 1}
        max={totalScreens}
        label={t('wizard.stepOf', { n: screenIndex + 1, total: totalScreens })}
        valueLabel={`${screenIndex + 1}/${totalScreens}`}
      />
      <h1 className="font-display text-[30px] font-semibold leading-tight text-ink">
        {t('wizard.programQuestion')}
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">{t('wizard.programHelp')}</p>

      {error !== undefined && error !== null && (
        <p role="alert" className="mt-4 text-[13px] text-danger">
          {toErrorMessage(t, error).title}
        </p>
      )}

      <QueryBoundary
        query={products}
        skeleton={<SkeletonList rows={2} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('wizard.noPrograms') }}
      >
        {(data) => (
          <ul className="mt-6 grid gap-4">
            {data.items.map((product) => (
              <li key={product.id}>
                <ProgramCard
                  product={product}
                  priceLabel={priceLabel(product, locale)}
                  trialLabel={
                    product.trialWorkoutCount > 0
                      ? t(trialBadgeKey(product.trialWorkoutCount), { n: product.trialWorkoutCount })
                      : null
                  }
                  chooseLabel={t('wizard.chooseProgram')}
                  loading={pendingSlug === product.slug}
                  onChoose={() => onChoose(product.slug)}
                />
              </li>
            ))}
          </ul>
        )}
      </QueryBoundary>

      <div className="mt-8">
        <Button variant="ghost" onClick={onBack}>
          {t('common.back')}
        </Button>
      </div>
    </section>
  );
}

function priceLabel(product: ProductOutput, locale: 'hr' | 'en'): string | null {
  const price = product.prices[0];
  return price ? formatMoney(price.amountCents, price.currency, locale) : null;
}

function ProgramCard({
  product,
  priceLabel: price,
  trialLabel,
  chooseLabel,
  loading,
  onChoose,
}: {
  product: ProductOutput;
  priceLabel: string | null;
  trialLabel: string | null;
  chooseLabel: string;
  loading: boolean;
  onChoose: () => void;
}) {
  return (
    <Card as="article" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="font-display text-[21px] font-semibold leading-tight text-ink">
          {product.name}
        </h2>
        {trialLabel && <Badge tone="accent">{trialLabel}</Badge>}
      </div>
      {product.description && (
        <p className="text-[14px] leading-relaxed text-ink-muted">{product.description}</p>
      )}
      {product.features.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {product.features.slice(0, 4).map((feature) => (
            <li key={feature} className="rounded-pill bg-bg-alt px-3 py-1 text-[12px] text-ink-muted">
              {feature}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
        {price && (
          <span className="font-display text-[21px] font-semibold text-accent-dark">{price}</span>
        )}
        <Button loading={loading} onClick={onChoose}>
          {chooseLabel}
        </Button>
      </div>
    </Card>
  );
}
